import React, { useState, useEffect } from 'react'
import { Project } from '../services/api'
import { TokenVestingSchedule, TokenFinancialData } from '../types/token'

interface TokenTimelineTabProps {
  projects: Project[]
}

interface ClientTokenData {
  project: Project
  vestingSchedule?: TokenVestingSchedule
  financialData?: TokenFinancialData
}

const TokenTimelineTab: React.FC<TokenTimelineTabProps> = ({ projects }) => {
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set())
  const [clientTokenData, setClientTokenData] = useState<ClientTokenData[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadTokenData()
  }, [projects])

  const loadTokenData = async () => {
    setLoading(true)
    try {
      // Fetch all token vesting schedules and financial data
      const [vestingResponse, financialResponse] = await Promise.all([
        fetch('http://localhost:61235/api/token-vesting'),
        fetch('http://localhost:61235/api/token-financial')
      ]);

      const vestingData = await vestingResponse.json();
      const financialData = await financialResponse.json();

      console.log('Fetched vesting data:', vestingData);
      console.log('Fetched financial data:', financialData);

      // Create a map of project names to their token data
      const vestingMap = new Map();
      const financialMap = new Map();

      if (vestingData.success && vestingData.data) {
        vestingData.data.forEach((vesting: any) => {
          vestingMap.set(vesting.project_name, vesting);
        });
      }

      if (financialData.success && financialData.data) {
        financialData.data.forEach((financial: any) => {
          financialMap.set(financial.client_name, financial);
        });
      }

                        // Project name mapping (spreadsheet names to database names)
                  const projectNameMapping: { [key: string]: string } = {
                    'Unite': 'Unite LTD',
                    'Unite (Superlayer)': 'Unite LTD',
                    'Towns': 'HERE NOT THERE LTD',
                    'Caesar': 'Caesar Data Limited',
                    'OpenLedger': 'Decentratech LTD',
                    'Rivalz': 'Rivalz',
                    'Gaia': 'Gaia',
                    'RHEA': 'RHEA',
                    'Thousands': 'Thousands',
                    'Valora (Divvi)': 'Valora',
                    'Open Game (Gemz)': 'Open Game',
                    'Freeze Tech (Dragonswap)': 'Freeze Tech',
                    'Kernel': 'Kernel',
                    'Pact Labs': 'Pact Labs',
                    'Pirate Nation (Proof of Play)': 'Pirate Nation',
                    'Arbitrum': 'Arbitrum'
                  };

      // Match projects with their token data
      const tokenData: ClientTokenData[] = projects.map(project => {
        // Try exact match first, then mapped name
        let vestingSchedule = vestingMap.get(project.client_name);
        let financialData = financialMap.get(project.client_name);

        if (!vestingSchedule && !financialData) {
          // Try mapped names
          const mappedName = projectNameMapping[project.client_name];
          if (mappedName) {
            vestingSchedule = vestingMap.get(mappedName);
            financialData = financialMap.get(mappedName);
          }
        }

        if (vestingSchedule || financialData) {
          return {
            project,
            vestingSchedule,
            financialData
          };
        }
        return null;
      }).filter(Boolean) as ClientTokenData[];

      console.log('Matched token data:', tokenData);
      setClientTokenData(tokenData);
    } catch (error) {
      console.error('Error loading token data:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleClient = (clientId: string) => {
    const newExpanded = new Set(expandedClients)
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId)
    } else {
      newExpanded.add(clientId)
    }
    setExpandedClients(newExpanded)
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'TBD'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusColor = (tgeDate: string) => {
    if (!tgeDate) return 'text-gray-400'
    const tge = new Date(tgeDate)
    const now = new Date()
    
    if (tge < now) return 'text-green-400'
    if (tge.getTime() - now.getTime() < 30 * 24 * 60 * 60 * 1000) return 'text-yellow-400'
    return 'text-blue-400'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Management Snapshot Header */}
      <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm rounded-lg border border-white/10 p-6">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-white mb-2">Token Portfolio Management Snapshot</h2>
          <p className="text-gray-300 text-sm">Real-time overview of token vesting, financial metrics, and risk assessment</p>
        </div>
        
        {/* Key Metrics Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white/5 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-400">
              {clientTokenData.filter(c => c.financialData?.token_potential_score && c.financialData.token_potential_score > 5).length}
            </div>
            <div className="text-xs text-gray-400">High Potential</div>
          </div>
          <div className="bg-white/5 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-yellow-400">
              {clientTokenData.filter(c => c.financialData?.token_potential_score && c.financialData.token_potential_score > 0 && c.financialData.token_potential_score <= 5).length}
            </div>
            <div className="text-xs text-gray-400">Medium Risk</div>
          </div>
          <div className="bg-white/5 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-400">
              {clientTokenData.filter(c => c.vestingSchedule?.tge_date && new Date(c.vestingSchedule.tge_date) < new Date()).length}
            </div>
            <div className="text-xs text-gray-400">Completed TGE</div>
          </div>
          <div className="bg-white/5 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-purple-400">
              {clientTokenData.filter(c => c.vestingSchedule?.tge_date && new Date(c.vestingSchedule.tge_date) > new Date()).length}
            </div>
            <div className="text-xs text-gray-400">Upcoming TGE</div>
          </div>
        </div>
        
        {/* Portfolio Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white/5 rounded-lg p-3">
            <div className="text-gray-400 mb-1">Total Monthly Revenue</div>
            <div className="text-white font-semibold">
              ${clientTokenData.reduce((sum, c) => sum + (c.financialData?.monthly_cash_retainer || 0), 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-white/5 rounded-lg p-3">
            <div className="text-gray-400 mb-1">Total Token Value (FDV)</div>
            <div className="text-white font-semibold">
              ${clientTokenData.reduce((sum, c) => sum + (c.financialData?.assumed_fdv || 0), 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-white/5 rounded-lg p-3">
            <div className="text-gray-400 mb-1">Average Token Score</div>
            <div className="text-white font-semibold">
              {clientTokenData.filter(c => c.financialData?.token_potential_score).length > 0 
                ? (clientTokenData.reduce((sum, c) => sum + (c.financialData?.token_potential_score || 0), 0) / clientTokenData.filter(c => c.financialData?.token_potential_score).length).toFixed(1)
                : '0.0'}/10
            </div>
          </div>
        </div>
      </div>

      {clientTokenData.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">No token data available</div>
          <div className="text-gray-500 text-sm">Clients with token vesting schedules will appear here</div>
        </div>
      ) : (
        <div className="space-y-3">
          {clientTokenData.map(({ project, vestingSchedule, financialData }) => (
            <div key={project.id} className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
              {/* Client Header - Always Visible */}
              <div 
                className="p-4 cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => toggleClient(project.id!.toString())}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <div>
                      <h3 className="text-white font-semibold">{project.client_name}</h3>
                                                    <div className="flex items-center space-x-4 text-sm text-gray-300">
                                <span>TGE: {formatDate(vestingSchedule?.tge_date || financialData?.tge_date)}</span>
                                <span className={getStatusColor(vestingSchedule?.tge_date || financialData?.tge_date || '')}>
                                  {(vestingSchedule?.tge_date || financialData?.tge_date) && new Date(vestingSchedule?.tge_date || financialData?.tge_date || '') < new Date() ? 'Completed' : 'Upcoming'}
                                </span>
                                <span>Chain: {vestingSchedule?.chain || 'N/A'}</span>
                                <span>Wallet: {vestingSchedule?.wallet_type || 'N/A'}</span>
                                {financialData?.token_percentage && (
                                  <span>Token: {financialData.token_percentage}%</span>
                                )}
                              </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-gray-400">
                      {financialData?.token_percentage || vestingSchedule ? 'Token Data' : 'No Token Data'}
                    </div>
                    <div className={`w-2 h-2 rounded-full ${
                      expandedClients.has(project.id!.toString()) ? 'bg-blue-500' : 'bg-gray-500'
                    }`}></div>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedClients.has(project.id!.toString()) && (
                <div className="border-t border-white/10 p-4 space-y-4">
                  {/* Vesting Schedule */}
                  {vestingSchedule && (
                    <div className="space-y-3">
                      <h4 className="text-white font-medium">Vesting Schedule</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">Next Vest Date:</span>
                          <div className="text-white">{formatDate(vestingSchedule.next_vest_date)}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Day 1 Unlock:</span>
                          <div className="text-white">{vestingSchedule.day_1_unlock ? 'Yes' : 'No'}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Material Transaction:</span>
                          <div className="text-white">{vestingSchedule.material_transaction_amount ? 'Yes' : 'No'}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Chain:</span>
                          <div className="text-white">{vestingSchedule.chain || 'N/A'}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Wallet Owner:</span>
                          <div className="text-white">{vestingSchedule.wallet_owner || 'N/A'}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">Wallet Type:</span>
                          <div className="text-white">{vestingSchedule.wallet_type || 'N/A'}</div>
                        </div>
                        <div>
                          <span className="text-gray-400">MM Needed:</span>
                          <div className="text-white">{vestingSchedule.mm_needed_at_tge || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  )}

                                                {/* Financial Data */}
                              {financialData && (
                                <div className="space-y-3">
                                  <h4 className="text-white font-medium">Financial Details</h4>
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="text-gray-400">Monthly Retainer:</span>
                                      <div className="text-white">
                                        {financialData.monthly_cash_retainer ? `$${financialData.monthly_cash_retainer.toLocaleString()}` : 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Token %:</span>
                                      <div className="text-white">{financialData.token_percentage}%</div>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">FDV:</span>
                                      <div className="text-white">
                                        {financialData.assumed_fdv ? `$${financialData.assumed_fdv.toLocaleString()}` : 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Lock-up Period:</span>
                                      <div className="text-white">
                                        {financialData.lock_up_months ? `${financialData.lock_up_months} months` : 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Vesting Period:</span>
                                      <div className="text-white">
                                        {financialData.vesting_months ? `${financialData.vesting_months} months` : 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">TGE Unlock %:</span>
                                      <div className="text-white">
                                        {financialData.tge_unlock_percentage ? `${financialData.tge_unlock_percentage}%` : 'N/A'}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Token Score:</span>
                                      <div className="text-white">{financialData.token_potential_score || 0}/10</div>
                                    </div>
                                    <div>
                                      <span className="text-gray-400">Unlock Terms:</span>
                                      <div className="text-white">{financialData.unlock_terms || 'N/A'}</div>
                                    </div>
                                    {financialData.additional_terms && (
                                      <div className="col-span-2">
                                        <span className="text-gray-400">Additional Terms:</span>
                                        <div className="text-white">{financialData.additional_terms}</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default TokenTimelineTab 